import React, { useState } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { TextInput } from '~components/common'
import { Process3 } from '~types'

interface EmailVerificationProps {
  process: Process3 | null
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ process }) => {
  const [emailSent, setEmailSent] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [email, setEmail] = useState('')
  const validEmail = email.includes('@') && email.includes('.')

  const handleSendLink = async (email: string): Promise<void> => {
    if (!process) return
    setSubmitted(true)
    try {
      await apiZodios.contacts_emailSignup({
        email: email,
        verify_options: {
          type: 'apply',
          apply: {
            process: process,
          },
        },
      })
      setEmailSent(true)
    } catch (error) {
      console.error('Failed to send verification:', error)
    } finally {
      setSubmitted(false)
    }
  }

  return emailSent ? (
    <Box sx={{ py: 3 }}>
      <Typography variant='h6' color='success.main' sx={{ fontSize: '20px' }}>
        Please check your email for a link to the online application.
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Box>
          <Typography variant='body1' component='span'>
            Did not receive an email?{' '}
          </Typography>
        </Box>
        <Typography
          variant='body1'
          sx={{
            textDecoration: 'underline',
            color: 'primary.main',
            cursor: 'pointer',
            mt: 1,
          }}
          onClick={() => {
            setEmailSent(false)
            setSubmitted(false)
          }}
        >
          Click to send a new verification email
        </Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={{ py: 3 }}>
      <Typography sx={{ mb: 2 }}>Please enter your email below for verification.</Typography>
      <Typography color='textSecondary' sx={{ mb: 3 }}>
        You'll receive an email link to complete the application online.
      </Typography>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!submitted) handleSendLink(email)
        }}
      >
        <TextInput
          key='email-input'
          fullWidth
          type='email'
          label='Email Address'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={email !== '' && !validEmail}
          placeholder='example@example.com'
          sx={{ mb: 3, maxWidth: '350px' }}
        />
        <Button
          variant='contained'
          type='submit'
          disabled={submitted || !validEmail}
          sx={{
            bgcolor: 'common.black',
            '&:hover': { bgcolor: 'grey.900' },
            borderRadius: '20px',
            px: 3,
          }}
        >
          {submitted ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1, color: 'common.white' }} />
              Sending Email Verification Link
            </>
          ) : (
            'Verify Email To Begin'
          )}
        </Button>
      </form>
    </Box>
  )
}

export default EmailVerification
